import React from 'react';

import { styled } from 'styled-components';

import Checkmark from 'assets/checkmark.svg';

import { sanitizeAndUpdatePreviewPaths } from 'utils/textUtils';

const InnerHtmlWrapper = styled.div<{ $fontWeight?: string; $textColor?: string }>`
  display: inline;
  color: ${(props) => props.$textColor ?? 'inherit'};

  h1,
  h2,
  h3,
  h4,
  h5 {
    font-family: ${(props) => props.theme.fonts.fontFamilySecondary};
    text-transform: uppercase;
  }

  h2 {
    font-size: 3rem;
    @media screen and (max-width: ${(props) => props.theme.breakpoints.md}) {
      font-size: 2rem;
    }
  }

  h3 {
    font-size: 2rem;
    @media screen and (max-width: ${(props) => props.theme.breakpoints.md}) {
      font-size: 1.5rem;
    }
  }

  h4 {
    font-size: 1.5rem;
    @media screen and (max-width: ${(props) => props.theme.breakpoints.md}) {
      font-size: 1.25rem;
    }
  }

  h5 {
    font-size: 1.25rem;
    @media screen and (max-width: ${(props) => props.theme.breakpoints.md}) {
      font-size: 1.15rem;
    }
  }

  span {
    color: inherit;
  }

  p,
  li {
    ${({ $fontWeight }) => $fontWeight && `font-weight: ${$fontWeight};`}
  }

  a {
    color: ${(props) => props.theme.colors.black};
    transition: 0.3s;

    &:hover {
      color: ${(props) => props.theme.colors.primaryRed};
    }
  }

  a.ctaButton {
    border: 1px solid ${(props) => props.theme.colors.ttRed};
    background-color: ${(props) => props.theme.colors.ttRed};
    text-decoration: none;

    color: ${(props) => props.theme.colors.white};
    font-family: ${(props) => props.theme.fonts.default};
    text-align: center;
    text-transform: uppercase;
    white-space: nowrap;
    border-radius: 5rem;
    justify-content: center;
    align-items: center;
    padding: 0.75rem 1.5rem;
    font-weight: 600;
    transition: background-color 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
    display: inline-flex;

    &:hover {
      background-color: ${(props) => props.theme.colors.darkerRed};
      color: ${(props) => props.theme.colors.white};
      border-color: ${(props) => props.theme.colors.darkerRed};
    }
  }

  ul.checkmark {
    list-style-type: none;
    padding-left: 1.5rem;
  }

  ul.checkmark li {
    background-image: url(${Checkmark});
    background-position: 0 4px;
    background-repeat: no-repeat;
    background-size: 1rem;
    margin-bottom: 0.75rem;
    margin-left: -1rem;
    padding-left: 1.75rem;
    list-style-type: none;
  }

  ul.checkmark-fill {
    list-style-type: none;
    padding-left: 0;
  }

  ul.checkmark-fill li {
    position: relative;
    padding-left: 2.5rem;
    margin-bottom: 1rem;
  }

  ul.checkmark-fill li:before {
    position: absolute;
    top: 0.125rem;
    left: 0;
    content: '\\f00c';
    background-color: ${(props) => props.theme.colors.ttRed};
    color: ${(props) => props.theme.colors.white};
    border-radius: 50%;
    justify-content: center;
    align-items: center;
    width: 1.5rem;
    height: 1.5rem;
    font-family: ${(props) => props.theme.fonts.faSolid900};
    font-size: 0.875rem;
    display: inline-flex;
    margin-right: 1rem;
  }

  li::marker {
    color: red;
    font-size: 1.5rem;
  }

  .center {
    text-align: center;
  }

  img {
    max-width: 100%;
  }

  figure {
    padding-bottom: 56.25%;
    width: 100%;
    max-width: 100%;
    text-align: center;
    clear: both;
    margin-left: auto;
    margin-right: auto;
    display: block;
    height: 0;
    position: relative;
    margin-top: 2rem;
    margin-bottom: 2rem;

    div {
      padding-bottom: inherit;
      display: inline-block;
      width: 100%;

      iframe {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
      }
    }
  }

  .video-wrapper {
    height: 0;
    padding-bottom: 56.25%;
    position: relative;

    // using !important here to ensure youtube defaults or anything inline is overriden
    iframe {
      height: 100% !important;
      left: 0;
      position: absolute;
      top: 0;
      width: 100% !important;
    }
  }
`;

interface InnerHtmlProps {
  className?: string;
  richText?: string;
  fontWeight?: string;
  textColor?: string;
}

const InnerHtml = ({ className, richText, fontWeight, textColor }: InnerHtmlProps) => (
  <InnerHtmlWrapper
    className={className}
    dangerouslySetInnerHTML={{ __html: sanitizeAndUpdatePreviewPaths(richText ?? '') }}
    $fontWeight={fontWeight}
    $textColor={textColor}
  />
);

export const UnstyledInnerHtml = ({ richText, className }: InnerHtmlProps) => (
  <div className={className} dangerouslySetInnerHTML={{ __html: sanitizeAndUpdatePreviewPaths(richText ?? '') }} />
);

export default InnerHtml;
